/**
 * TimelineSection -- 커플 타임라인(연애 히스토리) 편집
 */

import { ArrowUpDown, Heart, Plus, X } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Input, Textarea, Button, DatePicker } from '../../ui/index.js'
import type { TimelineItem } from '@cugumi/shared/schemas'
import styles from './SectionForm.module.css'

export function TimelineSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  const items: TimelineItem[] = (invitation as any).timeline ?? []

  function setItems(next: TimelineItem[]) {
    updateNested('timeline', next)
  }

  function addItem() {
    setItems([...items, { date: '', title: '', description: '' }])
  }

  function removeItem(index: number) {
    setItems(items.filter((_, i) => i !== index))
  }

  function updateItem(index: number, field: keyof TimelineItem, value: string) {
    const next = items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    setItems(next)
  }

  function sortItems() {
    const sorted = [...items].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
    setItems(sorted)
  }

  return (
    <div className={styles.form}>
      <div className={styles.personCard}>
        <div className={styles.personCardHeader}>
          <span className={styles.personCardIconBadge}>
            <Heart size={20} />
          </span>
          <span className={styles.personCardLabel}>타임라인</span>
          <div className={styles.personCardHeaderActions}>
            {items.length >= 2 && (
              <Button variant="ghost" size="sm" onClick={sortItems}>
                <ArrowUpDown size={14} /> 정렬
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={addItem}>
              <Plus size={14} /> 추가
            </Button>
          </div>
        </div>
        <div className={styles.personCardDivider} />
        <div className={styles.personCardBody}>
          {items.length === 0 && (
            <p className={styles.hint}>타임라인 항목을 추가하세요</p>
          )}

          {items.map((item, i) => (
            <div key={i} className={styles.fieldGroup}>
              <div className={styles.itemHeader}>
                <div className={styles.itemLabelGroup}>
                  <span className={styles.itemLabel}>항목 {i + 1}</span>
                  {item.date && <span className={styles.itemTime}>· {item.date}</span>}
                </div>
                <button
                  type="button"
                  className={styles.itemDeleteBtn}
                  onClick={() => removeItem(i)}
                  aria-label={`항목 ${i + 1} 삭제`}
                >
                  <X size={14} />
                </button>
              </div>
              <div className={styles.row}>
                <DatePicker
                  label="날짜"
                  value={item.date || null}
                  onChange={(_date, formatted) => updateItem(i, 'date', formatted)}
                  placeholder="날짜 선택"
                />
                <Input
                  label="제목"
                  value={item.title}
                  onChange={(e) => updateItem(i, 'title', e.target.value)}
                  placeholder="첫 만남"
                />
              </div>
              <Textarea
                label="설명"
                value={item.description ?? ''}
                onChange={(e) => updateItem(i, 'description', e.target.value)}
                placeholder="우연히 만난 그 날..."
                rows={2}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
